import {
  Box,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Link,
  Button,
  Spinner,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { ClientStatsRow } from '@/components/dashboard'
import {
  dashboardService,
  ClientDashboardStats,
} from '@/lib/services/dashboardService'
import { format, parseISO } from 'date-fns'

const STATUS_BADGE: Record<string, string> = {
  '0': 'gray',
  '1': 'blue',
  '2': 'yellow',
  '3': 'orange',
  '4': 'green',
  '5': 'gray',
  '6': 'red',
}

const STATUS_LABEL: Record<string, string> = {
  '0': 'Draft',
  '1': 'New',
  '2': 'Pending',
  '3': 'In Progress',
  '4': 'Resolved',
  '5': 'Closed',
  '6': 'Cancelled',
}

export default function DashboardPage() {
  const colors = useThemeColors()
  const [clientStats, setClientStats] = useState<ClientDashboardStats | null>(null)
  const [latestRequests, setLatestRequests] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [requestsLoading, setRequestsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const stats = await dashboardService.getClientDashboardStats()
        setClientStats(stats)
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err)
        setError(err.message || 'Failed to load dashboard data')
      } finally {
        setIsLoading(false)
      }
    }
    fetchDashboardData()
  }, [])

  useEffect(() => {
    const fetchLatestRequests = async () => {
      try {
        setRequestsLoading(true)
        const { data } = await dashboardService.getLatestTicketRequests()
        setLatestRequests(data)
      } catch {
        setLatestRequests([])
      } finally {
        setRequestsLoading(false)
      }
    }
    fetchLatestRequests()
  }, [])

  // Client-styled stats row will be used to display main request metrics
  // Data will be passed to the `ClientStatsRow` component below.

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column" w="100%" minW={0}>
        <VStack spacing={4} align="stretch" flex="1" overflow="hidden" minH={0} w="100%">
          {/* Error Alert */}
          {error && (
            <Alert status="error" borderRadius="md" flexShrink={0}>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Main Statistics Cards (Client design) */}
          {clientStats && (
            <Box flexShrink={0} w="100%" minW={0}>
              <ClientStatsRow
                totalRequests={(typeof clientStats.total_requests === 'number' && clientStats.total_requests > 0)
                  ? clientStats.total_requests
                  : ((clientStats.in_progress || 0) + (clientStats.open_requests || 0) + (clientStats.resolved_requests || 0))}
                inProgress={clientStats.in_progress ?? 0}
                openRequests={clientStats.open_requests ?? 0}
                resolvedRequests={clientStats.resolved_requests ?? 0}
              />
            </Box>
          )}

          {/* Empty State */}
          {!isLoading && !clientStats && (
            <Box
              flexShrink={0}
              p={8}
              textAlign="center"
              borderRadius="md"
              bg={colors.cardBg}
              border="1px"
              borderColor={colors.border}
            >
              <Text color={colors.textMuted} fontSize="sm">
                No dashboard data available. Data will appear here once the system starts collecting statistics.
              </Text>
            </Box>
          )}

          {/* Latest 10 updated ticket requests */}
          <Card 
            flex="1" 
            minH={0} 
            display="flex" 
            flexDirection="column" 
            overflow="hidden"
            bg={colors.cardBg} 
            borderWidth="1px" 
            borderColor={colors.border}
          >
            <CardBody 
              flex="1" 
              minH={0} 
              display="flex" 
              flexDirection="column" 
              overflow="hidden"
              p={4}
            >
              <VStack align="stretch" spacing={4} flex="1" minH={0} overflow="hidden">
                <Box display="flex" justifyContent="space-between" alignItems="center" flexShrink={0}>
                  <Text fontWeight="semibold" fontSize="lg" color={colors.text}>
                    Latest updated requests
                  </Text>
                  <Button
                    as={RouterLink}
                    to="/my-request"
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                  >
                    View all
                  </Button>
                </Box>
                {requestsLoading ? (
                  <Box py={6} textAlign="center" flexShrink={0}>
                    <Spinner size="md" color="blue.500" />
                  </Box>
                ) : latestRequests.length === 0 ? (
                  <Text color={colors.textMuted} fontSize="sm" py={4} flexShrink={0}>
                    No requests yet.
                  </Text>
                ) : (
                  <Box 
                    flex="1" 
                    minH={0} 
                    overflowY="auto" 
                    overflowX="hidden"
                    w="100%"
                  >
                    <Table size="sm" variant="simple" w="100%">
                      <Thead position="sticky" top={0} bg={colors.cardBg} zIndex={1}>
                        <Tr>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">Request #</Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">Service</Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">Category</Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">Status</Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">Date created</Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">Last updated</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {latestRequests.map((row: any) => (
                          <Tr key={row.id}>
                            <Td whiteSpace="nowrap">
                              <Link
                                as={RouterLink}
                                to={`/my-request`}
                                color="blue.500"
                                fontSize="sm"
                                title={row.request_number}
                              >
                                {row.request_number ?? '—'}
                              </Link>
                            </Td>
                            <Td whiteSpace="nowrap">{row.service_type ?? '—'}</Td>
                            <Td whiteSpace="nowrap">{row.category ?? '—'}</Td>
                            <Td whiteSpace="nowrap">
                              <Badge
                                colorScheme={STATUS_BADGE[String(row.ticket_status)] ?? 'gray'}
                                variant="solid"
                                fontSize="xs"
                              >
                                {STATUS_LABEL[String(row.ticket_status)] ?? row.ticket_status_label ?? '—'}
                              </Badge>
                            </Td>
                            <Td whiteSpace="nowrap">
                              {row.created_at
                                ? (() => {
                                    try {
                                      return format(
                                        typeof row.created_at === 'string' ? parseISO(row.created_at) : new Date(row.created_at),
                                        'MMM d, yyyy · h:mm a'
                                      )
                                    } catch {
                                      return row.created_at
                                    }
                                  })()
                                : '—'}
                            </Td>
                            <Td whiteSpace="nowrap" color={colors.textSecondary} fontSize="sm">
                              {row.last_updated_at_human ?? '—'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
